import AddIcon from "@mui/icons-material/AddRounded";
import { Button, Typography } from "@mui/material";
import type {
  GridCellParams,
  GridColDef,
  GridEventListener,
  GridValueFormatter,
  GridValueGetter,
  GridValueSetter,
} from "@mui/x-data-grid-premium";
import { parseISO } from "date-fns";
import { useCallback, useMemo, useState } from "react";
import type { ObjectMetricExtension } from "../../../../api/dataCollectionTypes";
import type { ScenarioTableBlockColumn, SingleExtensionBasedBlockRow } from "../../../../api/inputFormTypes";
import type {
  MetricDataType,
  MetricExtensionConfiguration,
  MetricFormatConfiguration,
} from "../../../../api/portfolioMonitoringTypes";
import useDefinedContext from "../../../../hooks/useDefinedContext";
import useSingleClickGridEditing from "../../../../hooks/useSingleClickGridEditing";
import { formatDateOnly } from "../../../../utilities/dateUtils";
import { makeNextEditableCellEdited } from "../../../../utilities/editableDataGridHelper";
import { formatMetricNumberValue } from "../../../../utilities/metricFormatters";
import { defined } from "../../../../utilities/typeHelper";
import DataGrid from "../../../grid/DataGrid";
import { getSubmissionDataGridFormStyles } from "../submissionTableFormStyles";
import useDataSubmissionFormContext from "../useDataSubmissionFormContext";
import AddMetricExtensionDialog from "./AddMetricExtensionDialog";
import type { InputFormGridCellValueType } from "./inputFormDataGridHelper";
import { metricDataTypeToGridColTypeMap } from "./inputFormDataGridHelper";
import PortfolioMonitoringInputTableContext, {
  PortfolioMonitoringInputTableContextProvider,
} from "./PortfolioMonitoringInputTableContext";
import ScenarioColumnHeader from "./ScenarioColumnHeader";
import { calculateTotals } from "./tableTotalsCalculator";

type RowModel = SingleExtensionBasedBlockRow;

interface NameHeaderProps {
  extensionConfiguration: MetricExtensionConfiguration;
  metricId: string;
  metricName: string;
}

const NameHeader = ({ extensionConfiguration, metricId, metricName }: NameHeaderProps) => {
  const { isSubmissionEditable } = useDataSubmissionFormContext();
  const { onAddMetricExtensions } = useDefinedContext(PortfolioMonitoringInputTableContext);

  return (
    <>
      <Typography variant="subtitle2" noWrap sx={{ flexGrow: 1 }}>
        {extensionConfiguration.extensionName}
      </Typography>
      {isSubmissionEditable && (
        <Button
          variant="text"
          startIcon={<AddIcon />}
          tabIndex={-1}
          onClick={() => onAddMetricExtensions([metricId], metricName)}
        >
          Add
        </Button>
      )}
    </>
  );
};

const NameCell = ({ row }: { row: RowModel }) => {
  if (row.type === "SingleMetricExtensionOnly") {
    return (
      <Typography noWrap sx={{ pl: row.indentationLevel * 2, flexGrow: 1 }}>
        {row.extensionValue}
      </Typography>
    );
  }

  if (row.type === "Total") {
    return (
      <Typography variant="subtitle2" noWrap sx={{ pl: row.indentationLevel * 2 }}>
        {row.name}
      </Typography>
    );
  }

  return null;
};

const createNumericValueFormatter =
  (formatConfig: MetricFormatConfiguration | undefined): GridValueFormatter<RowModel> =>
  (value: number | null | undefined, row: RowModel) => {
    if (row.type === "Total") {
      return !value ? "-" : formatMetricNumberValue(value, { ...formatConfig, numberOfDecimals: 0 });
    }

    return typeof value === "number" ? formatMetricNumberValue(value, formatConfig) : "";
  };

const valueGetter: GridValueGetter<RowModel, InputFormGridCellValueType> = (_, row, col) => {
  if (row.type === "Total") {
    return row.values[col.field];
  }

  if (row.type === "SingleMetricExtensionOnly") {
    return row.values[col.field];
  }

  return undefined;
};

const dateValueGetter: GridValueGetter<RowModel, InputFormGridCellValueType> = (_, row, col) => {
  if (row.type === "Total") {
    return row.values[col.field];
  }

  if (row.type === "SingleMetricExtensionOnly") {
    const dateIso = row.values[col.field];
    return typeof dateIso === "string" ? parseISO(dateIso) : undefined;
  }

  return undefined;
};

const valueSetter: GridValueSetter<RowModel, InputFormGridCellValueType> = (newValue, row, col) => {
  if (row.type === "SingleMetricExtensionOnly") {
    const value = newValue instanceof Date ? formatDateOnly(newValue) : (newValue ?? undefined);

    return {
      ...row,
      values: {
        ...row.values,
        [col.field]: value,
      },
    };
  }

  return row;
};

const editableCellClassName = ({ row }: { row: RowModel }) => (row.type === "Total" ? "total-cell" : "");

const readonlyCellClassName = ({ row }: { row: RowModel }) => (row.type === "Total" ? "total-cell" : "readonly-cell");

const mapScenarioColumn = (
  columnDef: ScenarioTableBlockColumn,
  dataType: MetricDataType,
  formatConfig: MetricFormatConfiguration | undefined,
  isSubmissionEditable: boolean
): GridColDef<RowModel> => ({
  field: columnDef.id,
  flex: 1,
  minWidth: 160,
  type: metricDataTypeToGridColTypeMap[dataType],
  editable: isSubmissionEditable && columnDef.isEditable,
  renderHeader: () => <ScenarioColumnHeader columnDefinition={columnDef} />,
  headerClassName: columnDef.isEditable ? "" : "readonly-header",
  cellClassName: columnDef.isEditable ? editableCellClassName : readonlyCellClassName,
  valueGetter: dataType === "Date" ? dateValueGetter : valueGetter,
  valueSetter,
  valueFormatter: dataType === "Number" || dataType === "Money" ? createNumericValueFormatter(formatConfig) : undefined,
});

const getColumns = (
  columnDefinitions: ScenarioTableBlockColumn[],
  metricId: string,
  metricName: string,
  extensionConfiguration: MetricExtensionConfiguration,
  dataType: MetricDataType,
  formatConfig: MetricFormatConfiguration | undefined,
  isSubmissionEditable: boolean
): GridColDef<RowModel>[] => {
  const nameColumn: GridColDef<RowModel> = {
    field: "name",
    headerName: "Metric",
    flex: 1,
    minWidth: 160,
    editable: false,
    cellClassName: ({ row }) => (row.type === "Total" ? "total-cell" : ""),
    renderHeader: () => (
      <NameHeader extensionConfiguration={extensionConfiguration} metricId={metricId} metricName={metricName} />
    ),
    renderCell: ({ row }) => <NameCell row={row} />,
  };

  const scenarioColumns: GridColDef<RowModel>[] = columnDefinitions.map((columnDef) =>
    mapScenarioColumn(columnDef, dataType, formatConfig, isSubmissionEditable)
  );

  return [nameColumn, ...scenarioColumns];
};

const getRows = (rows: SingleExtensionBasedBlockRow[]): RowModel[] => {
  return calculateTotals(rows);
};

interface Props {
  blockId: string;
  metricId: string;
  metricName: string;
  dataType: MetricDataType;
  defaultFormat?: MetricFormatConfiguration;
  extensionConfiguration: MetricExtensionConfiguration;
  columnDefinitions: ScenarioTableBlockColumn[];
  rowDefinitions: SingleExtensionBasedBlockRow[];
}

interface AddExtensionDialogState {
  metricIds: string[];
  metricName: string;
  addedExtensionIds: string[];
}

const SingleExtendedMetricTableInputForm = ({
  blockId,
  metricId,
  metricName,
  dataType,
  defaultFormat,
  extensionConfiguration,
  columnDefinitions,
  rowDefinitions,
}: Props) => {
  const { isSubmissionEditable, onBlockCellValuesEdit } = useDataSubmissionFormContext();

  const { cellModesModel, setCellModesModel, handleCellClick } = useSingleClickGridEditing();

  const [rows, setRows] = useState<RowModel[]>(() => getRows(rowDefinitions));
  const [dialogState, setDialogState] = useState<AddExtensionDialogState>();

  const columns = useMemo(
    () =>
      getColumns(
        columnDefinitions,
        metricId,
        metricName,
        extensionConfiguration,
        dataType,
        defaultFormat,
        isSubmissionEditable
      ),
    [columnDefinitions, dataType, defaultFormat, extensionConfiguration, isSubmissionEditable, metricId, metricName]
  );

  const isCellEditable = useCallback(({ row }: { row: RowModel }) => row.type === "SingleMetricExtensionOnly", []);

  const handleRowUpdate = useCallback(
    (newRow: RowModel, oldRow: RowModel) => {
      if (newRow.type !== "SingleMetricExtensionOnly" || oldRow.type !== "SingleMetricExtensionOnly") {
        return newRow;
      }

      const updatedRow = { ...newRow };

      const updatedValueEntry = Object.entries(updatedRow.values).find(
        ([columnId, currentCellValue]) => oldRow.values[columnId] !== currentCellValue
      );

      if (updatedValueEntry !== undefined) {
        const [columnId, newCellValue] = updatedValueEntry;
        const colDef = columnDefinitions.find((col) => col.id === columnId);
        if (colDef !== undefined) {
          const metricExtensionId = updatedRow.extensionId;
          const fallbackValue = dataType === "Money" || dataType === "Number" ? 0 : "";
          const value = newCellValue ?? fallbackValue;
          onBlockCellValuesEdit(blockId, [{ metricId, metricExtensionId, columnId, valueUserInput: value }]);
        }
      }

      setTimeout(
        () =>
          setRows((prevRows) => {
            const newRows = prevRows.map((row) => {
              if (row.id !== updatedRow.id) {
                return row;
              }

              return { ...updatedRow };
            });

            return calculateTotals(newRows);
          }),
        100
      );

      return updatedRow;
    },
    [blockId, columnDefinitions, dataType, metricId, onBlockCellValuesEdit]
  );

  const handleAddMetricExtensions = useCallback(
    (metricIds: string[], metricName: string) => {
      const addedExtensionIds = rows
        .map((r) => (r.type === "SingleMetricExtensionOnly" ? r.extensionId : ""))
        .filter(Boolean);

      setDialogState({
        metricIds,
        metricName,
        addedExtensionIds,
      });
    },
    [rows]
  );

  const handleMetricExtensionAdded = useCallback((addedExtensions: ObjectMetricExtension[]) => {
    setDialogState(undefined);

    if (addedExtensions.length === 0) {
      return;
    }

    const extensionId = defined(addedExtensions[0]).id;
    const extensionValue = defined(addedExtensions[0]).value;

    setRows((prevRows) => {
      const newRow: RowModel = {
        type: "SingleMetricExtensionOnly",
        id: extensionValue,
        indentationLevel: 0,
        extensionId,
        extensionValue,
        values: {},
      };

      return [newRow, ...prevRows];
    });
  }, []);

  const handleCellKeyDown: GridEventListener<"cellKeyDown"> = useCallback(
    (params: GridCellParams<RowModel>, event) => {
      if (event.code !== "Tab") {
        return;
      }

      event.defaultMuiPrevented = true;
      event.preventDefault();
      const direction = event.shiftKey ? "previous" : "next";
      makeNextEditableCellEdited(direction, rows, columns, params, setCellModesModel, isCellEditable, (r) => r.id);
    },
    [columns, isCellEditable, rows, setCellModesModel]
  );

  return (
    <PortfolioMonitoringInputTableContextProvider onAddMetricExtensions={handleAddMetricExtensions}>
      <DataGrid<RowModel>
        columns={columns}
        rows={rows}
        columnHeaderHeight={52}
        pinnedColumns={{ left: ["name"] }}
        cellModesModel={cellModesModel}
        onCellModesModelChange={setCellModesModel}
        onCellClick={handleCellClick}
        onCellKeyDown={handleCellKeyDown}
        disableColumnReorder
        disableColumnSorting
        showCellVerticalBorder
        showColumnVerticalBorder
        editMode="cell"
        isCellEditable={isCellEditable}
        processRowUpdate={handleRowUpdate}
        noScrollableContainer
        sx={getSubmissionDataGridFormStyles}
        slots={{ noRowsOverlay: () => null }}
      />

      {dialogState && (
        <AddMetricExtensionDialog
          metricIds={dialogState.metricIds}
          metricName={dialogState.metricName}
          extensionConfigurationsPerMetricId={{ [metricId]: extensionConfiguration }}
          addedExtensionIds={dialogState.addedExtensionIds}
          allowCreatingExtensionValues={extensionConfiguration.staticExtensions.length === 0}
          onAdded={handleMetricExtensionAdded}
          onClose={() => setDialogState(undefined)}
        />
      )}
    </PortfolioMonitoringInputTableContextProvider>
  );
};

export default SingleExtendedMetricTableInputForm;
